/**
 * <s-gallery-dots> shows which product photo is in view on phones.
 * It watches the horizontally scrolling gallery that immediately precedes it
 * ([data-product-gallery]) and marks the matching [data-dot] with data-active.
 * Dots are decorative (aria-hidden); swiping remains the interaction.
 */
class GalleryDots extends HTMLElement {
  #gallery = null;
  #dots = [];
  #frame = 0;
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  connectedCallback() {
    const previous = this.previousElementSibling;
    this.#gallery = previous?.matches('[data-product-gallery]') ? previous : null;
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));
    if (!this.#gallery || this.#dots.length < 2) return;
    this.#gallery.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#gallery?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const gallery = this.#gallery;
    const slides = Array.from(gallery.children);
    if (!slides.length) return;
    const center = gallery.getBoundingClientRect().left + gallery.clientWidth / 2;
    let activeIndex = 0;
    let closest = Infinity;
    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });
    this.#dots.forEach((dot, index) => {
      dot.toggleAttribute('data-active', index === activeIndex);
    });
  }
}

if (!customElements.get('s-gallery-dots')) {
  customElements.define('s-gallery-dots', GalleryDots);
}
